import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Download, ArrowLeft, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { vngSteps } from "../data/vngSteps";
import { loadSystemForEdit } from "../lib/assessmentStore";
import { downloadSjabloonPdf } from "../lib/api";

/**
 * /sjabloon/:systemId — the HTML form for the official RiskaiΛ Compliance
 * Assessment Sjabloon. The fields (organisatie, systeem, datum, ondertekenaar
 * and the per-VNG-step bevindingen) are filled here and POSTed to the backend,
 * which compiles the sjabloon LaTeX EDT (riskaia-reporting). The signature
 * itself is a real PDF /Sig widget (xdp-sign) — not collected here.
 *
 * The per-step textareas are pre-filled with the canonical expert placeholders
 * (the same narratives the old jsPDF template carried), which the user edits.
 */
const Sjabloon = () => {
  const { systemId } = useParams();
  const navigate = useNavigate();

  // Pre-fill organisation / system from the system the user came from
  // (works for both Leeuwarden seeds and user assessments).
  const snap = systemId ? loadSystemForEdit(systemId) : null;

  const [meta, setMeta] = useState({
    organisation: snap?.form?.orgName || "Gemeente Leeuwarden",
    system_name: snap?.displayName || snap?.form?.systemName || "",
    assessment_date: new Date().toLocaleDateString("nl-NL"),
    signatory_name: "",
    signatory_role: "",
  });
  const [findings, setFindings] = useState(() =>
    Object.fromEntries(vngSteps.map((s) => [s.id, s.placeholder || ""]))
  );
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { tone: "ok"|"err", msg }

  const setMetaField = (k) => (e) => setMeta((m) => ({ ...m, [k]: e.target.value }));
  const setFinding = (id) => (e) =>
    setFindings((f) => ({ ...f, [id]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      const safe = (meta.system_name || "systeem")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      await downloadSjabloonPdf(
        {
          organisation: meta.organisation,
          system_name: meta.system_name,
          assessment_date: meta.assessment_date,
          version: "1.2",
          signatory_name: meta.signatory_name,
          signatory_role: meta.signatory_role,
          steps: vngSteps.map((s) => ({
            number: s.number,
            title: s.title,
            findings: findings[s.id] || "",
          })),
        },
        `riskaia-sjabloon-${safe}.pdf`
      );
      setStatus({ tone: "ok", msg: "Sjabloon gegenereerd en gedownload." });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[downloadSjabloonPdf] failed:", err);
      setStatus({ tone: "err", msg: "Genereren mislukt. Controleer de verbinding en probeer opnieuw." });
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "bg-transparent border-b border-[#C0C5CE]/40 focus:border-[#C0C5CE] py-2 text-white outline-none";

  return (
    <div data-testid="page-sjabloon">
      <section className="rk-container pt-20 md:pt-28 pb-10">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="overline mb-6 flex items-center gap-2 text-[#7FD7E4]"
          data-testid="sjabloon-back"
        >
          <ArrowLeft strokeWidth={1.25} className="w-3.5 h-3.5" /> Terug
        </button>
        <div className="overline mb-6">Officieel sjabloon</div>
        <h1
          className="text-5xl md:text-6xl text-white tracking-tight leading-[1.05] max-w-4xl"
          style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
        >
          Compliance Assessment Sjabloon
        </h1>
        <p className="mt-8 text-lg text-[#D4D8DF] max-w-2xl leading-relaxed">
          Vul de bevindingen per VNG-lifecyclestap in. De gegevens worden
          gebruikt om het officiële sjabloon te genereren; het
          handtekeningvlak blijft een echt PDF-handtekeningveld.
        </p>
      </section>

      <div className="hairline" />

      <form onSubmit={onSubmit} data-testid="sjabloon-form" className="rk-container py-12 md:py-16 space-y-12">
        {/* Metadata */}
        <fieldset className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <label className="flex flex-col gap-2">
            <span className="overline">Organisatie</span>
            <input type="text" value={meta.organisation} onChange={setMetaField("organisation")} className={inputCls} data-testid="sjabloon-organisation" />
          </label>
          <label className="flex flex-col gap-2">
            <span className="overline">Systeemnaam</span>
            <input type="text" value={meta.system_name} onChange={setMetaField("system_name")} className={inputCls} data-testid="sjabloon-system" />
          </label>
          <label className="flex flex-col gap-2">
            <span className="overline">Datum</span>
            <input type="text" value={meta.assessment_date} onChange={setMetaField("assessment_date")} className={inputCls} data-testid="sjabloon-date" />
          </label>
          <label className="flex flex-col gap-2">
            <span className="overline">Naam functionaris</span>
            <input type="text" value={meta.signatory_name} onChange={setMetaField("signatory_name")} className={inputCls} data-testid="sjabloon-signatory-name" />
          </label>
          <label className="flex flex-col gap-2">
            <span className="overline">Functie</span>
            <input type="text" value={meta.signatory_role} onChange={setMetaField("signatory_role")} className={inputCls} data-testid="sjabloon-signatory-role" />
          </label>
        </fieldset>

        {/* Per-step findings, pre-filled with the canonical placeholders */}
        <div className="space-y-8">
          {vngSteps.map((s) => (
            <label key={s.id} className="block" data-testid={`sjabloon-step-${s.id}`}>
              <span className="overline" style={{ color: "#7FD7E4" }}>
                Stap {s.number} · {s.title}
              </span>
              {s.legal && (
                <span className="block mt-1 mb-2 text-[11px] tracking-[0.18em] uppercase text-[#9AA4B2]">
                  EU AI Act · {s.legal.article} — {s.legal.topic}
                </span>
              )}
              <textarea
                rows={5}
                value={findings[s.id]}
                onChange={setFinding(s.id)}
                className="mt-2 w-full bg-transparent border border-[#C0C5CE]/40 focus:border-[#C0C5CE] p-4 text-[#D4D8DF] outline-none resize-y leading-relaxed"
              />
            </label>
          ))}
        </div>

        {/* Submit + status */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
          <div className="min-h-[1.5rem]">
            {status && status.tone === "ok" && (
              <span className="flex items-center gap-2 text-[#9FE2BF]" data-testid="sjabloon-status-ok">
                <CheckCircle2 strokeWidth={1.25} className="w-4 h-4" /> {status.msg}
              </span>
            )}
            {status && status.tone === "err" && (
              <span className="flex items-center gap-2 text-[#E8A0A0]" data-testid="sjabloon-status-err">
                <AlertCircle strokeWidth={1.25} className="w-4 h-4" /> {status.msg}
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="sjabloon-submit"
            className="rk-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                Genereren… <Loader2 strokeWidth={1.25} className="w-4 h-4 animate-spin" />
              </>
            ) : (
              <>
                Genereer &amp; download sjabloon <Download strokeWidth={1.25} className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Sjabloon;
